'use client'

import { useApi } from '@repo/api'
import { useAuth } from '@repo/auth'
import { z } from '@repo/schema'
import { pageDataValueSchema } from '@repo/schema/page-data'
import { useMutation } from '@repo/tanstack/query'
import type { Atom } from '@repo/tanstack/store'
import { useEffect, useMemo, useRef, useState } from 'react'

import { usePathname } from '../adapters/next'
import {
  type ColumnSizingState,
  type PersistedColumnPinningState,
  type RowData,
  type Table,
  type TableFeatures,
  type TableState,
  toPersistedColumnPinning,
  toTableColumnPinning,
} from '../tanstack-table'
import {
  type ColumnSettingDefs,
  type ColumnSettings,
  isOrderVersionStale,
  keepUnknownSettings,
  type MergedTableSettings,
  mergeTableSettings,
  NO_COLUMN_SETTINGS,
  normalizeBackendTableSettings,
  type StoredTableSettings,
} from './merge-table-settings'
import type { TableColumnOwner } from './table-column-owner'
import {
  createTableSettingsSaveController,
  type TableSettingsSaveHandler,
} from './table-settings-save-controller'

type PersistedTableState = Pick<
  TableState<TableFeatures>,
  'columnOrder' | 'columnPinning' | 'columnResizing' | 'columnSizing' | 'columnVisibility'
> & { columnSettings: ColumnSettings }

type SerializedSettings = {
  columnSettings: string
  order: string
  pinning: string
  sizing: string
  visibility: string
}

const EMPTY_COLUMN_IDS: readonly string[] = []

const getPersistedTableState = <TData extends RowData>(
  store: Table<TData, object>['store'],
  columnSettings: Atom<ColumnSettings>,
  nonPersistedColumnIds: readonly string[] = [],
): PersistedTableState => {
  const state = store.get()

  const excludedIds = new Set(nonPersistedColumnIds)
  const filterIds = (ids: string[]) => ids.filter(id => !excludedIds.has(id))
  const filterRecord = <TValue>(record: Record<string, TValue>) =>
    Object.fromEntries(Object.entries(record).filter(([id]) => !excludedIds.has(id)))

  return {
    columnOrder: filterIds(state.columnOrder),
    columnPinning: {
      end: filterIds(state.columnPinning.end),
      start: filterIds(state.columnPinning.start),
    },
    columnResizing: state.columnResizing,
    columnSettings: columnSettings.get(),
    columnSizing: filterRecord(state.columnSizing),
    columnVisibility: filterRecord(state.columnVisibility),
  }
}

const serializeSettings = (state: PersistedTableState): SerializedSettings => ({
  columnSettings: JSON.stringify(state.columnSettings),
  order: JSON.stringify(state.columnOrder),
  pinning: JSON.stringify(state.columnPinning),
  sizing: JSON.stringify(state.columnSizing),
  visibility: JSON.stringify(state.columnVisibility),
})

const toStoredSettings = (
  state: PersistedTableState,
  sizing: ColumnSizingState,
  orderVersion: number | undefined,
): StoredTableSettings => ({
  ...state.columnSettings,
  order: state.columnOrder,
  orderVersion,
  pinning: toPersistedColumnPinning(state.columnPinning),
  sizing,
  visibility: state.columnVisibility,
})

/**
 * Columns as an owner stores them: what the table shows, with every column's visibility and every
 * Field selection so a column or field added later never changes them, plus what the source holds
 * that the table cannot show.
 */
const toOwnerColumns = (
  owner: TableColumnOwner,
  columns: StoredTableSettings,
  tableColumnIds: ReadonlySet<string>,
  columnSettingDefs: ColumnSettingDefs,
) =>
  keepUnknownSettings(
    {
      ...columns,
      visibility: Object.fromEntries(
        [...tableColumnIds].map(id => [id, columns.visibility?.[id] ?? true]),
      ),
    },
    owner.getSource().columns,
    tableColumnIds,
    columnSettingDefs,
  )

export type TableSettingsSaveStatus = 'error' | 'saved' | 'saving'

type TableSettingsMutationVariables = {
  pathnameSlug: string
  settings: TableSettingsPayload
  userId: number | string
}

type TableSettingsPayload = z.output<typeof pageDataValueSchema>

const getTableSettingsCacheKey = (userId: number | string | undefined, pathnameSlug: string) =>
  userId ? `table-settings-${userId}-${pathnameSlug}` : null

const loadTableSettingsFromCache = (cacheKey: null | string) => {
  if (!cacheKey) return null

  try {
    const cached = localStorage.getItem(cacheKey)
    return cached ? JSON.parse(cached) : null
  } catch (error) {
    console.error('Failed to load cached table settings:', error)
    return null
  }
}

const saveTableSettingsToCache = (cacheKey: null | string, settings: TableSettingsPayload) => {
  if (!cacheKey) return

  try {
    localStorage.setItem(cacheKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to cache table settings:', error)
  }
}

/**
 * Hook for managing table user settings persistence
 *
 * FEATURES:
 * - Column visibility: Show/hide columns
 * - Column sizing: Remember column widths
 * - Column pinning: Pin columns to left/right
 * - Column ordering: Custom column order
 * - Auto-save with debouncing for performance
 * - Local storage caching for instant loads
 *
 * HOW IT WORKS:
 * 1. On mount: Load from localStorage (instant) then sync with backend
 * 2. On change: Apply immediately to UI (optimistic update)
 * 3. Debounced save: Batch changes and save after 500ms of inactivity
 * 4. Settings scoped to: user ID + page pathname
 *
 * BACKEND STORAGE:
 * - Endpoint: /users/{userId}/pages/{pageSlug}/data
 * - Storage format: {
 *     table: {
 *       visibility: { columnId: boolean },
 *       sizing: { columnId: number },
 *       pinning: { left: string[], right: string[] },
 *       order: string[]
 *     }
 *   }
 * - Page identification: URL pathname → slug (e.g., /loads/active → loads-active)
 *
 * PERFORMANCE:
 * - Debounced API calls (500ms)
 * - Local storage for instant perceived performance
 * - Optimistic UI updates
 */
export const useTableUserSettings = <TData extends RowData = RowData>({
  columnOrderVersion,
  columnOwner,
  columnSettingDefs,
  columnSettings,
  initialColumnOrder,
  initialColumnPinning,
  initialColumnSizing,
  initialColumnVisibility,
  knownColumnsRollout,
  lockedColumnPinning,
  nonPersistedColumnIds = EMPTY_COLUMN_IDS,
  postRolloutColumnIds,
  requiredColumnOrder = EMPTY_COLUMN_IDS,
  requiredColumnPinning,
  requiredColumnSizing,
  requiredColumnVisibility,
  table,
}: {
  columnOrderVersion?: number
  /** Replaces PageData and local-storage persistence when set. */
  columnOwner?: TableColumnOwner
  columnSettingDefs: ColumnSettingDefs
  /** The table's per-column settings, set from stored settings like the column state. */
  columnSettings: Atom<ColumnSettings>
  initialColumnOrder: string[]
  initialColumnPinning: PersistedColumnPinningState
  initialColumnSizing: ColumnSizingState
  initialColumnVisibility: Record<string, boolean>
  knownColumnsRollout?: string
  lockedColumnPinning?: PersistedColumnPinningState
  nonPersistedColumnIds?: readonly string[]
  postRolloutColumnIds?: readonly string[]
  requiredColumnOrder?: readonly string[]
  requiredColumnPinning?: PersistedColumnPinningState
  requiredColumnSizing?: ColumnSizingState
  requiredColumnVisibility?: Record<string, boolean>
  table: Table<TData, object>
}) => {
  const pathname = usePathname()
  const api = useApi()
  const { user } = useAuth()
  const userId = user?.id
  const store = table.store
  const setColumnOrder = table.setColumnOrder
  const setColumnPinning = table.setColumnPinning
  const setColumnSizing = table.setColumnSizing
  const setColumnVisibility = table.setColumnVisibility
  const getAllLeafColumns = table.getAllLeafColumns

  /**
   * Convert pathname to a slug for backend storage key
   * Examples:
   * - "/" → "dashboard"
   * - "/loads" → "loads"
   * - "/loads/active" → "loads-active"
   */
  const pathnameSlug =
    pathname === '/' ? 'dashboard' : pathname.replace(/^\/+/, '').replace(/\//g, '-')
  const initialSettings = useMemo(
    () => ({
      columnSettingDefs,
      knownColumnsRollout,
      order: initialColumnOrder,
      orderVersion: columnOrderVersion,
      pinning: initialColumnPinning,
      postRolloutColumnIds,
      sizing: initialColumnSizing,
      visibility: initialColumnVisibility,
    }),
    [
      columnOrderVersion,
      columnSettingDefs,
      initialColumnOrder,
      initialColumnPinning,
      initialColumnSizing,
      initialColumnVisibility,
      knownColumnsRollout,
      postRolloutColumnIds,
    ],
  )
  const cacheKey = columnOwner ? null : getTableSettingsCacheKey(userId, pathnameSlug)
  const [saveStatus, setSaveStatus] = useState<TableSettingsSaveStatus>('saved')

  /**
   * Mutation for persisting table settings to backend
   */
  const updateTableSettingsMutation = useMutation({
    meta: {
      invalidates: [],
    },
    mutationFn: async ({
      pathnameSlug: targetPathnameSlug,
      settings,
      userId: targetUserId,
    }: TableSettingsMutationVariables) => {
      await api.users.pageData.save.mutate({
        input: settings,
        path: { page: targetPathnameSlug, userId: targetUserId },
      })
    },
  })

  const saveMutation = updateTableSettingsMutation.mutateAsync

  const pendingPayloadRef = useRef<null | TableSettingsPayload>(null)
  const resetPayloadRef = useRef<null | TableSettingsPayload>(null)

  const [saveController] = useState(() => createTableSettingsSaveController<TableSettingsPayload>())
  const {
    cancel: cancelSave,
    flush: flushSave,
    queue: queueSave,
    saveImmediately,
    setSaveHandler,
  } = saveController

  useEffect(() => {
    let isCommitted = true
    const handler: TableSettingsSaveHandler<TableSettingsPayload> = {
      onError: (_settings, error) => {
        console.error('Failed to save table settings:', error)
        if (isCommitted) setSaveStatus('error')
      },
      onSuccess: settings => {
        if (resetPayloadRef.current !== settings) {
          saveTableSettingsToCache(cacheKey, settings)
        }
        resetPayloadRef.current = null
        if (isCommitted) setSaveStatus('saved')
      },
      save: async settings => {
        if (!userId) return
        await saveMutation({ pathnameSlug, settings, userId })
      },
    }

    setSaveHandler(handler)
    return () => {
      isCommitted = false
    }
  }, [cacheKey, pathnameSlug, saveMutation, setSaveHandler, userId])

  const previousValuesRef = useRef<null | SerializedSettings>(null)
  const persistedSizingRef = useRef<ColumnSizingState>(initialColumnSizing)
  const wasResizingRef = useRef(false)
  const isHydratingRef = useRef(false)
  const hasLocalChangesRef = useRef(false)
  const applyMergedRef = useRef<
    ((_stored: StoredTableSettings | undefined) => MergedTableSettings) | null
  >(null)
  /**
   * Version writes should carry. Tracks the highest version this session has seen, so an older
   * client that reads settings from a newer deploy never stamps them back down to its own.
   */
  const effectiveOrderVersionRef = useRef<number | undefined>(columnOrderVersion)
  /**
   * The owner source the table last applied, and the settings it showed then. Outlives re-runs
   * of the effect below.
   */
  const appliedSourceRef = useRef<null | {
    owner: TableColumnOwner
    revision: number
    shown: string
  }>(null)
  useEffect(() => {
    if (!userId) return

    let isActive = true
    const nonPersistedColumnIdSet = new Set(nonPersistedColumnIds)
    const requiredColumnOrderSet = new Set(requiredColumnOrder)
    const getCurrentState = () =>
      getPersistedTableState(store, columnSettings, nonPersistedColumnIds)
    const tableColumnIds = new Set(initialSettings.order)

    const applyMerged = (stored: StoredTableSettings | undefined) => {
      if (!isActive) {
        return {
          columnSettings: NO_COLUMN_SETTINGS,
          order: [],
          orderMigrated: false,
          orderVersion: initialSettings.orderVersion,
          pinning: { left: [], right: [] },
          sizing: {},
          visibility: {},
        }
      }

      const merged = mergeTableSettings({
        columnOrderVersion: initialSettings.orderVersion,
        columnSettingDefs: initialSettings.columnSettingDefs,
        initialColumnOrder: initialSettings.order,
        initialColumnPinning: initialSettings.pinning,
        initialColumnSizing: initialSettings.sizing,
        initialColumnVisibility: initialSettings.visibility,
        knownColumnsRollout: initialSettings.knownColumnsRollout,
        lockedColumnPinning,
        postRolloutColumnIds: initialSettings.postRolloutColumnIds,
        stored,
      })
      const persistedOrder = merged.order.filter(id => !nonPersistedColumnIdSet.has(id))
      const effectiveMerged = { ...merged, order: persistedOrder }
      effectiveOrderVersionRef.current = merged.orderVersion
      const mergedPinning = {
        left: [
          ...new Set([...(requiredColumnPinning?.left ?? []), ...(merged.pinning.left ?? [])]),
        ],
        right: [
          ...new Set([...(merged.pinning.right ?? []), ...(requiredColumnPinning?.right ?? [])]),
        ],
      }
      const resolvedPinning = toTableColumnPinning(mergedPinning)

      isHydratingRef.current = true
      columnSettings.set(merged.columnSettings)
      setColumnVisibility({ ...merged.visibility, ...requiredColumnVisibility })
      setColumnSizing({ ...merged.sizing, ...requiredColumnSizing })
      setColumnPinning(resolvedPinning)
      setColumnOrder([
        ...requiredColumnOrder,
        ...persistedOrder.filter(id => !requiredColumnOrderSet.has(id)),
      ])
      isHydratingRef.current = false
      hasLocalChangesRef.current = false

      const currentState = getCurrentState()
      previousValuesRef.current = serializeSettings(currentState)
      persistedSizingRef.current = currentState.columnSizing
      wasResizingRef.current = Boolean(currentState.columnResizing.isResizingColumn)

      return effectiveMerged
    }

    applyMergedRef.current = applyMerged

    const handleStoreChange = () => {
      if (!isActive || isHydratingRef.current) return

      const state = getCurrentState()
      const currentValues = serializeSettings(state)
      const isResizing = Boolean(state.columnResizing.isResizingColumn)
      const resizeStarted = !wasResizingRef.current && isResizing
      const resizeJustEnded = wasResizingRef.current && !isResizing
      wasResizingRef.current = isResizing
      if (resizeStarted) hasLocalChangesRef.current = true

      if (!previousValuesRef.current) {
        previousValuesRef.current = currentValues
        persistedSizingRef.current = state.columnSizing
        return
      }

      const previousValues = previousValuesRef.current
      const sizingChanged = resizeJustEnded && previousValues.sizing !== currentValues.sizing
      const hasChanged =
        previousValues.columnSettings !== currentValues.columnSettings ||
        previousValues.visibility !== currentValues.visibility ||
        previousValues.pinning !== currentValues.pinning ||
        previousValues.order !== currentValues.order ||
        (resizeJustEnded && sizingChanged)

      if (!hasChanged) return

      hasLocalChangesRef.current = true
      const sizing = sizingChanged ? state.columnSizing : persistedSizingRef.current
      previousValuesRef.current = {
        ...currentValues,
        sizing: sizingChanged ? currentValues.sizing : previousValues.sizing,
      }
      if (sizingChanged) persistedSizingRef.current = state.columnSizing

      // Carry the order stamp forward, else a user edit would un-stamp it and re-run the
      // migration on the next mount, discarding the order they just chose. Uses the
      // effective version so an older client cannot downgrade a newer deploy's stamp.
      const table = toStoredSettings(state, sizing, effectiveOrderVersionRef.current)
      const settings: z.infer<typeof pageDataValueSchema> = { table }

      // An owner decides when to persist; the table only reports what it shows.
      if (columnOwner) {
        return columnOwner.report(
          toOwnerColumns(columnOwner, table, tableColumnIds, initialSettings.columnSettingDefs),
          sizingChanged ? 'resized' : 'edited',
        )
      }

      pendingPayloadRef.current = settings
      resetPayloadRef.current = null
      setSaveStatus('saving')
      queueSave(settings)
    }

    const subscription = store.subscribe(handleStoreChange)
    const columnSettingsSubscription = columnSettings.subscribe(handleStoreChange)

    if (columnOwner) {
      const getShown = () => {
        const current = getCurrentState()
        return toOwnerColumns(
          columnOwner,
          toStoredSettings(current, current.columnSizing, effectiveOrderVersionRef.current),
          tableColumnIds,
          initialSettings.columnSettingDefs,
        )
      }
      const getShownKey = () => JSON.stringify(serializeSettings(getCurrentState()))
      const isApplied = () =>
        appliedSourceRef.current?.owner === columnOwner &&
        appliedSourceRef.current.revision === columnOwner.getSource().revision
      const applySource = () => {
        const { columns, revision } = columnOwner.getSource()
        applyMerged(columns)
        appliedSourceRef.current = { owner: columnOwner, revision, shown: getShownKey() }
        columnOwner.report(getShown(), 'applied')
      }

      // Only a new revision discards local edits. A re-run for anything else, such as column
      // definitions built inline on every render, applies the source again, so columns defined
      // since take their saved state, then puts back any edits the table showed.
      const hasEdits = isApplied() && appliedSourceRef.current?.shown !== getShownKey()
      const shown = getShown()
      applySource()
      if (hasEdits) {
        applyMerged(shown)
        columnOwner.report(getShown(), 'edited')
      }
      const blankVisibility = getAllLeafColumns()
        .filter(column => !nonPersistedColumnIdSet.has(column.id))
        .map(column => [column.id, !column.getCanHide()] as const)
      // A table without columns yet has no Blank start to offer.
      columnOwner.reportBlank(
        blankVisibility.length > 0 ? { visibility: Object.fromEntries(blankVisibility) } : null,
      )
      const unsubscribeOwner = columnOwner.subscribe(() => {
        if (!isApplied()) applySource()
      })

      return () => {
        isActive = false
        unsubscribeOwner()
        columnOwner.reportBlank(null)
        subscription.unsubscribe()
        columnSettingsSubscription.unsubscribe()
        flushSave()
        applyMergedRef.current = null
        previousValuesRef.current = null
        hasLocalChangesRef.current = false
      }
    }

    const cachedSettings = loadTableSettingsFromCache(cacheKey)
    if (cachedSettings?.table) {
      applyMerged(cachedSettings.table)
    } else {
      const currentState = getCurrentState()
      previousValuesRef.current = serializeSettings(currentState)
      persistedSizingRef.current = currentState.columnSizing
    }

    api.users.pageData.get
      .query({ path: { page: pathnameSlug, userId } })
      .then(res => {
        if (!isActive) return
        const safeBackend = normalizeBackendTableSettings(res?.value?.table)
        if (safeBackend && !hasLocalChangesRef.current) {
          const merged = applyMerged(safeBackend)
          const settings: z.infer<typeof pageDataValueSchema> = {
            table: {
              // Match what was pushed into the store so cache and store agree on column order.
              order: merged.order,
              orderVersion: merged.orderVersion,
              pinning: merged.pinning,
              sizing: merged.sizing,
              visibility: merged.visibility,
            },
          }
          // Persist the stamp immediately (not debounced — the cleanup cancels pending saves)
          // so the forced order lands exactly once and never re-fires on later mounts.
          if (isOrderVersionStale(safeBackend.orderVersion, initialSettings.orderVersion)) {
            pendingPayloadRef.current = settings
            resetPayloadRef.current = null
            setSaveStatus('saving')
            saveImmediately(settings)
          } else {
            saveTableSettingsToCache(cacheKey, settings)
          }
        }
      })
      .catch(error => {
        if (isActive) console.error('Failed to load table settings:', error)
      })

    return () => {
      isActive = false
      subscription.unsubscribe()
      columnSettingsSubscription.unsubscribe()
      flushSave()
      applyMergedRef.current = null
      previousValuesRef.current = null
      hasLocalChangesRef.current = false
    }
  }, [
    cacheKey,
    columnOwner,
    columnSettings,
    initialSettings,
    lockedColumnPinning,
    nonPersistedColumnIds,
    pathnameSlug,
    requiredColumnOrder,
    requiredColumnPinning,
    requiredColumnSizing,
    requiredColumnVisibility,
    flushSave,
    queueSave,
    saveImmediately,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setColumnVisibility,
    store,
    getAllLeafColumns,
    api,
    userId,
  ])

  /**
   * Utility function to reset all settings to defaults
   */
  const resetSettings = () => {
    cancelSave()
    if (cacheKey) {
      localStorage.removeItem(cacheKey)
    }

    // Reset to initial defaults via the same merge path used at hydration
    applyMergedRef.current?.(undefined)
    // A reset is a local edit. Do not let an older backend read rehydrate stale settings.
    hasLocalChangesRef.current = true

    // Reset is distinct from an explicit empty layout: omit all layout fields from the backend.
    const resetPayload: TableSettingsPayload = {
      table: columnOrderVersion === undefined ? {} : { orderVersion: columnOrderVersion },
    }
    if (columnOwner) {
      const currentState = getPersistedTableState(
        table.store,
        columnSettings,
        nonPersistedColumnIds,
      )
      return columnOwner.report(
        toOwnerColumns(
          columnOwner,
          toStoredSettings(currentState, currentState.columnSizing, columnOrderVersion),
          new Set(initialColumnOrder),
          columnSettingDefs,
        ),
        'edited',
      )
    }
    pendingPayloadRef.current = resetPayload
    resetPayloadRef.current = resetPayload
    setSaveStatus('saving')
    saveImmediately(resetPayload)
  }

  const retrySave = () => {
    const pendingPayload = pendingPayloadRef.current
    if (!pendingPayload) return
    setSaveStatus('saving')
    saveImmediately(pendingPayload)
  }

  return {
    isLoading: saveStatus === 'saving',
    resetSettings,
    retrySave,
    saveStatus,
  }
}
